// ABOUTME: File and image cell renderer for PIM data tables, previewing the media and editing it inline.
// ABOUTME: In edit mode a dialog hosts the EditNode file field, which owns the upload and media-lookup calls.
import { Dialog, EditIcon, IconButton, Stack } from '@ds/coolshop';
import {
    type CustomCellRendererProps,
    type NodeContentValueO,
    useCellEditCommit,
    useEditingCellReporter,
} from '@ds/data-table';
import AssetFieldElement from '@src/components/formFields/AssetFieldElement';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { isMediaColumn } from '../asGridContentType';
import GridCellImageRender from './GridCellImageRender';
import { StandaloneField, writeLanguageEntry } from './StandaloneField';

/**
 * Serves both the `image` and the `file` content types: they share the same editor and differ only
 * in how the field previews the media.
 */
export default function GridCellFileRender(props: CustomCellRendererProps) {
    const { value, contentsValue, contexts, row, editableOpt, ct, DefaultRenderer } = props;
    const intl = useIntl();

    const [isOpen, setIsOpen] = useState(false);
    const [entries, setEntries] = useState(value as NodeContentValueO[]);

    const { commit, isLoading } = useCellEditCommit({
        editableOpt,
        contentsValue,
        nodeId: row.id,
        hierarchy: row.hierarchy,
        ct,
        row,
    });
    // An open editor is a pending edit: another user's save of this cell becomes a conflict.
    useEditingCellReporter(
        row.id,
        contentsValue?.content
            ? `${contentsValue.content.contentTypeGroup}-${contentsValue.content.contentType}`
            : undefined,
        isOpen,
    );

    // A media column is handed to the package as `image`, so a cell with no media content — a
    // folder's asset — lands here rather than on the media renderer. The package's own image
    // cell would draw a placeholder and an empty preview for it; the media renderer draws nothing.
    if (isMediaColumn(ct)) {
        return <GridCellImageRender {...props} />;
    }

    // The read display is the design system's own thumbnail/filename cell.
    if (!editableOpt) {
        return <DefaultRenderer />;
    }

    const isImage = contentsValue.content.type === 'image';
    const isMultiValue = !!ct?.multiValue;
    const { defaultLanguage } = contexts.language;

    const currentEntry =
        entries?.find(entry => entry.languageIsoCode === defaultLanguage) ?? entries?.[0];
    const storedMedia = (Array.isArray(currentEntry?.value) ? currentEntry!.value : []).map(String);
    // The field holds an array for multi-value content and a plain string otherwise.
    const initialValue = isMultiValue ? storedMedia : storedMedia[0] ?? '';

    const handleSave = async (edited: unknown) => {
        const media = (Array.isArray(edited) ? edited : [edited])
            .filter(entry => entry !== null && entry !== undefined && entry !== '')
            .map(String);
        const nextEntries = writeLanguageEntry(entries ?? [], defaultLanguage, media);
        const saved = await commit({ values: nextEntries }, media.join(', '));
        if (saved) {
            setEntries(nextEntries);
            setIsOpen(false);
        }
    };

    return (
        <Stack direction="row" alignItems="center">
            <DefaultRenderer />
            <IconButton
                aria-label={intl.formatMessage({
                    id: 'search-detail.edit-cell.media-edit',
                    defaultMessage: 'Edit media',
                })}
                size="small"
                color="primary"
                disabled={isLoading}
                onClick={e => {
                    e.stopPropagation();
                    setIsOpen(true);
                }}
            >
                <EditIcon fontSize="small" />
            </IconButton>
            <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth="sm" fullWidth>
                <Stack
                    onClick={e => {
                        e.stopPropagation();
                    }}
                >
                    <StandaloneField
                        initialValue={initialValue}
                        isMultiValue={isMultiValue}
                        label={intl.formatMessage({
                            id: 'search-detail.edit-cell.media',
                            defaultMessage: 'Media',
                        })}
                        saving={isLoading}
                        onCancel={() => setIsOpen(false)}
                        onSave={handleSave}
                    >
                        {fieldProps => (
                            <AssetFieldElement
                                {...(fieldProps as any)}
                                type={isImage ? 'preview' : 'list'}
                                accept={isImage ? 'image/*' : undefined}
                                // `isImage` here and not only the flag: the URL flow is the one
                                // PIM-2818 built for images, and this renderer also serves `file`.
                                allowExternalUrl={isImage && !!ct?.allowExternalUrl}
                            />
                        )}
                    </StandaloneField>
                </Stack>
            </Dialog>
        </Stack>
    );
}
